import React, { useState, useEffect } from "react";
import { useTaskManager } from "@/contexts/TaskManager";
import { useSafeWindowManager } from "@/hooks/useSafeWindowManager";
import TaskCreatorWindow from "@/components/windows/TaskCreatorWindow";
import {
  WindowHeader,
  WindowContent,
  WindowGrid,
  WindowSection
} from "@/components/ui/window-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckSquare,
  Plus,
  Search,
  Filter,
  Clock,
  AlertTriangle,
  Target,
  User
} from "lucide-react";
import { cn } from "@/lib/utils";
import TaskDetailsWindow from "@/components/windows/TaskDetailsWindow.tsx";

export default function AllTasksWindow() {
  const { tasks, loading, error, refreshTasks } = useTaskManager();
  const { windowManager } = useSafeWindowManager();
  const { openWindow, closeWindow } = windowManager;

  const [searchQuery, setSearchQuery] = useState("");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  useEffect(() => {
    console.log('[AllTasksWindow] Component mounted');
    console.log('[AllTasksWindow] Loading:', loading);
    console.log('[AllTasksWindow] Error:', error);
    console.log('[AllTasksWindow] Tasks:', tasks);
  }, [loading, error, tasks]);

  // Открытие окна создания задачи
  const handleCreateTask = () => {
    openWindow(
        'task-creator',
        'Create New Task',
        <TaskCreatorWindow onClose={() => closeWindow('task-creator')} />,
        {
          appType: 'task-creator'
        }
    );
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
      case "high":
        return "border-red-500/30 text-red-400 bg-red-500/10";
      case "medium":
        return "border-amber-500/30 text-amber-400 bg-amber-500/10";
      case "low":
        return "border-green-500/30 text-green-400 bg-green-500/10";
      default:
        return "border-blue-500/30 text-blue-400 bg-blue-500/10";
    }
  };

  const getStatusBadge = (progress: number) => {
    if (progress === 100) {
      return <Badge className="border-green-500/30 text-green-400 bg-green-500/10 text-xs">Completed</Badge>;
    } else if (progress > 0) {
      return <Badge className="border-blue-500/30 text-blue-400 bg-blue-500/10 text-xs">In Progress</Badge>;
    } else {
      return <Badge className="border-gray-500/30 text-gray-400 bg-gray-500/10 text-xs">Pending</Badge>;
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPriority = filterPriority === "all" || task.priority === filterPriority;
    const matchesStatus = filterStatus === "all" ||
        (filterStatus === "completed" && task.progress === 100) ||
        (filterStatus === "in-progress" && task.progress > 0 && task.progress < 100) ||
        (filterStatus === "pending" && task.progress === 0);

    return matchesSearch && matchesPriority && matchesStatus;
  });

  const handleTaskClick = (task: any) => {
    openWindow(
        `task-details-${task.id}`,
        `Task: ${task.title}`,
        <TaskDetailsWindow task={task} />
    )
    }

  if (loading) {
    return (
        <div className="h-full flex flex-col bg-transparent">
          <WindowHeader
              title="All Tasks"
              subtitle="Loading tasks..."
              icon={<CheckSquare className="w-6 h-6" />}
          />
          <WindowContent>
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          </WindowContent>
        </div>
    );
  }

  if (error) {
    return (
        <div className="h-full flex flex-col bg-transparent">
          <WindowHeader
              title="All Tasks"
              subtitle="Error loading tasks"
              icon={<CheckSquare className="w-6 h-6" />}
          />
          <WindowContent>
            <div className="flex flex-col items-center justify-center h-full space-y-4">
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg max-w-md">
                <p className="text-red-400 font-semibold mb-2">Error loading tasks:</p>
                <p className="text-red-300 text-sm">{error}</p>
              </div>
              <Button onClick={() => window.location.reload()} variant="outline">
                Reload Page
              </Button>
              <details className="text-xs text-muted-foreground max-w-md">
                <summary className="cursor-pointer hover:text-foreground">Debug Info</summary>
                <div className="mt-2 p-3 bg-black/50 rounded border border-white/10 font-mono">
                  <div>Error: {error}</div>
                  <div>Tasks count: {tasks.length}</div>
                  <div>Loading: {loading.toString()}</div>
                </div>
              </details>
            </div>
          </WindowContent>
        </div>
    );
  }

  return (
      <div className="h-full flex flex-col bg-transparent">
        <WindowHeader
            title="All Tasks"
            subtitle={`${filteredTasks.length} ${filteredTasks.length === 1 ? 'task' : 'tasks'}`}
            icon={<CheckSquare className="w-6 h-6" />}
            actions={
              <Button
                  onClick={handleCreateTask}
                  size="sm"
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                New Task
              </Button>
            }
        />

        <WindowContent>
          {/* Filters */}
          <div className="mb-6 space-y-4">
            <div className="flex gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10"
                />
              </div>

              {/* Priority Filter */}
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-40 bg-white/5 border-white/10">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/20 backdrop-blur-xl">
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40 bg-white/5 border-white/10">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/20 backdrop-blur-xl">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {filteredTasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <CheckSquare className="w-16 h-16 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No tasks found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {searchQuery || filterPriority !== "all" || filterStatus !== "all"
                      ? "Try adjusting your filters"
                      : "Create your first task to get started"}
                </p>
                <Button onClick={handleCreateTask} className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Create Task
                </Button>
              </div>
          ) : (
              <div className="space-y-3">
                {filteredTasks.map((task) => (
                    <div onClick={() => handleTaskClick(task)}
                        key={task.id}
                        className="p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-foreground">{task.title}</h3>
                            <Badge className={cn("capitalize text-xs", getPriorityColor(task.priority))}>
                              {task.priority}
                            </Badge>
                            {getStatusBadge(task.progress)}
                          </div>
                          <p className="text-sm text-muted-foreground">{task.description}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        {task.category && (
                            <div className="flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              <span>{task.category}</span>
                            </div>
                        )}
                        {task.assignee && (
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              <span>{task.assignee}</span>
                            </div>
                        )}
                        {task.dueTime && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>{task.dueTime}</span>
                            </div>
                        )}
                        <div className="ml-auto">
                          <span className="text-xs font-medium">{task.progress}% complete</span>
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="w-full bg-white/10 rounded-full h-1.5">
                          <div
                              className="bg-blue-500 h-1.5 rounded-full transition-all"
                              style={{ width: `${task.progress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                ))}
              </div>
          )}
        </WindowContent>
      </div>
  );
}

export { AllTasksWindow };